'use client';

import { ThreadPrimitive, useThreadRuntime } from '@assistant-ui/react';
import { CustomUserMessage, CustomAssistantMessage } from './message-renderer';
import { Composer } from './composer';
import { Button } from '@/components/ui/button';
import { LightbulbIcon, Loader2 } from 'lucide-react';
import { useGoalAwareSuggestions } from '@/hooks/useGoalAwareSuggestions';
import { SuggestedPromptsBar } from '@/components/momento/SuggestedPromptsBar';
import { GoalsButton } from '@/components/momento/GoalsButton';
import { SpeakerContextIndicator } from './speaker-context-indicator';
import { useSyncExternalStore, useState, useEffect } from 'react';

/**
 * Main Thread Component
 * Uses assistant-ui primitives for message rendering, scrolling, and state management
 * Guide Mode: Always active - shows goal-aware suggestions after assistant responses
 */
export function Thread() {
  const threadRuntime = useThreadRuntime();

  const threadState = useSyncExternalStore(
    threadRuntime.subscribe,
    () => threadRuntime.getState(),
    () => threadRuntime.getState()
  );

  const messages = threadState.messages;
  const isRunning = threadState.isRunning;
  const lastMessage = messages[messages.length - 1];

  // Extract last assistant message content for suggestion parsing
  const lastAssistantContent = lastMessage?.role === 'assistant'
    ? lastMessage.content?.filter((p: any) => p.type === 'text').map((p: any) => p.text).join('\n') || ''
    : undefined;

  // Extract tool results for workflow suspension detection
  const lastAssistantToolResults = lastMessage?.role === 'assistant'
    ? lastMessage.content?.filter((p: any) => p.type === 'tool-result').map((p: any) => p.result) || []
    : [];

  const { suggestions } = useGoalAwareSuggestions(lastAssistantContent, lastAssistantToolResults);
  const showSuggestions = !isRunning && lastMessage?.role === 'assistant' && suggestions.length > 0;

  // Analyzing indicator state - shows after user sends message while waiting for response
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const [analyzingDots, setAnalyzingDots] = useState('');
  const [statusMessage, setStatusMessage] = useState('Analyzing your request');

  // Map tool names to user-friendly status messages
  const getToolStatusMessage = (toolName: string): string => {
    const toolMessages: Record<string, string> = {
      'search-moments': 'Searching your videos',
      'create-content-workflow': 'Creating your post',
      'discover-content-workflow': 'Discovering content',
      'extract-speaker-quotes-workflow': 'Finding quotes',
      'export-moment': 'Starting export',
      'export-memo': 'Exporting memo',
      'analyze-speaker-tone': 'Analyzing speaker tone',
      'generate-speaker-post': 'Generating post',
      'get-speaker-info': 'Getting speaker info',
      'create-memo': 'Creating memo',
      'update-memo': 'Updating memo',
      'create-collection': 'Creating collection',
      'list-collections': 'Loading collections',
      'get-transcript': 'Loading transcript',
      'export-transcript': 'Exporting transcript',
    };
    return toolMessages[toolName] || 'Processing';
  };

  // Detect active tool calls and update status message
  useEffect(() => {
    if (!isRunning) {
      setStatusMessage('Analyzing your request');
      return;
    }

    // Check last assistant message for in-progress tool calls
    const assistantMessages = messages.filter(m => m.role === 'assistant');
    const lastAssistant = assistantMessages[assistantMessages.length - 1];

    if (lastAssistant?.content) {
      // Find tool-call parts that don't have corresponding results yet
      const toolCalls = lastAssistant.content.filter((p: any) => p.type === 'tool-call');
      const toolResults = lastAssistant.content.filter((p: any) => p.type === 'tool-result');
      const resultIds = new Set(toolResults.map((r: any) => r.toolCallId));

      // Find tool calls without results (still running)
      const runningTools = toolCalls.filter((tc: any) => !resultIds.has(tc.toolCallId));

      if (runningTools.length > 0) {
        const latestTool = runningTools[runningTools.length - 1] as any;
        setStatusMessage(getToolStatusMessage(latestTool.toolName));
        return;
      }
    }

    // Default to analyzing if no active tools detected
    setStatusMessage('Analyzing your request');
  }, [isRunning, messages]);

  // Check if assistant has produced any text content yet
  const assistantHasTextContent = (() => {
    if (lastMessage?.role !== 'assistant') return false;
    const textParts = lastMessage.content?.filter((p: any) => p.type === 'text' && p.text?.trim());
    return textParts && textParts.length > 0;
  })();

  useEffect(() => {
    // Show analyzing indicator when running and no text response yet
    // This keeps the indicator visible during tool execution
    const shouldShow = isRunning && !assistantHasTextContent;

    console.log('[Thread] Status indicator check:', {
      isRunning,
      assistantHasTextContent,
      shouldShow,
      lastMessageRole: lastMessage?.role,
      messagesCount: messages.length
    });

    if (shouldShow) {
      // Small delay to avoid flashing for instant responses
      const showTimer = setTimeout(() => setShowAnalyzing(true), 150);
      return () => clearTimeout(showTimer);
    } else {
      setShowAnalyzing(false);
    }
  }, [isRunning, assistantHasTextContent, lastMessage?.role, messages.length]);

  // Animate the dots for visual feedback
  useEffect(() => {
    if (!showAnalyzing) {
      setAnalyzingDots('');
      return;
    }

    const interval = setInterval(() => {
      setAnalyzingDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);

    return () => clearInterval(interval);
  }, [showAnalyzing]);

  return (
    <ThreadPrimitive.Root className="flex flex-col h-full relative">
      {/* Thread header with context and Goals button */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background/95 backdrop-blur-sm">
        <SpeakerContextIndicator />
        <GoalsButton />
      </div>

      {/* Scrollable messages area with built-in auto-scroll */}
      <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Welcome message with suggestions */}
          <ThreadPrimitive.Empty>
            <div className="flex items-center justify-center h-full text-center">
              <div className="space-y-4 max-w-2xl">
                <p className="text-lg font-medium text-muted-foreground">
                  How can I help you today?
                </p>
                <p className="text-sm text-muted-foreground">
                  Try searching for moments, creating memos, or asking about your videos.
                </p>

                {/* Contextual suggestions in empty state */}
                <div className="flex flex-wrap gap-2 justify-center mt-6">
                  <ThreadPrimitive.Suggestion
                    prompt="Search for customer testimonials"
                    send
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-sm hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-950"
                    >
                      <LightbulbIcon className="w-3.5 h-3.5 mr-1.5" />
                      Search for customer testimonials
                    </Button>
                  </ThreadPrimitive.Suggestion>
                  <ThreadPrimitive.Suggestion
                    prompt="Who are my speakers?"
                    send
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-sm hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-950"
                    >
                      <LightbulbIcon className="w-3.5 h-3.5 mr-1.5" />
                      Who are my speakers?
                    </Button>
                  </ThreadPrimitive.Suggestion>
                  <ThreadPrimitive.Suggestion
                    prompt="Create a LinkedIn post"
                    send
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-sm hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-950"
                    >
                      <LightbulbIcon className="w-3.5 h-3.5 mr-1.5" />
                      Create a LinkedIn post
                    </Button>
                  </ThreadPrimitive.Suggestion>
                  <ThreadPrimitive.Suggestion
                    prompt="Show me recent uploads"
                    send
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-sm hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-950"
                    >
                      <LightbulbIcon className="w-3.5 h-3.5 mr-1.5" />
                      Show me recent uploads
                    </Button>
                  </ThreadPrimitive.Suggestion>
                </div>
              </div>
            </div>
          </ThreadPrimitive.Empty>

          {/* Message list with custom renderers */}
          <ThreadPrimitive.Messages
            components={{
              UserMessage: CustomUserMessage,
              AssistantMessage: CustomAssistantMessage,
            }}
          />

          {/* Analyzing indicator - shows while waiting for agent response */}
          {showAnalyzing && (
            <div className="flex items-start gap-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              </div>
              <div className="flex-1 pt-1">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/50">
                  <span className="text-sm text-muted-foreground">
                    {statusMessage}{analyzingDots}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ThreadPrimitive.Viewport>

      {/* Scroll to bottom button (shown when not at bottom) */}
      <ThreadPrimitive.ScrollToBottom className="absolute bottom-24 right-8 z-10">
        <button className="rounded-full bg-background shadow-md border p-2 hover:bg-accent transition-colors">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-foreground"
          >
            <path
              d="M8 12L3 7L4.4 5.6L8 9.2L11.6 5.6L13 7L8 12Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </ThreadPrimitive.ScrollToBottom>

      {/* Goal-aware suggestions after assistant responses */}
      {showSuggestions && (
        <SuggestedPromptsBar
          suggestions={suggestions}
          className="border-t bg-background/80 backdrop-blur-sm"
        />
      )}

      {/* Composer at bottom */}
      <Composer />
    </ThreadPrimitive.Root>
  );
}
